if (!document.adgilebuild) {
  document.adgilebuild = document.adgilebuild || 'build-3719-6ee32a66dfbf9d0bb8251c6c01c719550a0ae8fa';
  console.log(document.adgilebuild);
}
import React, { Suspense, lazy } from 'react';

import { CssBaseline } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/styles';
import PrivateBoundary from 'components/Auth/PrivateBoundary';
import { Auth0Provider } from 'components/Auth/react-auth0-wrapper';
import ReactDOM from 'react-dom';
import { CacheProvider } from 'rest-hooks';
import { themeBrandingTokens } from 'theme/palette';
import theme from './theme';

// Keep this in sync with register/register/FetchInterceptor.svelte
const ACTIVE_SERVERS = {
  localhost: 'http://localhost:63785',
  'dev.catalyst.adgile.com': 'https://adgile-portalapi-dev.azurewebsites.net',
  'stage.catalyst.adgile.com': 'https://adgile-portalapi-stage.azurewebsites.net',
  'live.catalyst.adgile.com': 'https://adgile-portalapi.azurewebsites.net',
  'catalyst.adgile.com': 'https://adgile-portalapi.azurewebsites.net',
  'au.catalyst.adgile.com': 'https://adgile-portalapi.azurewebsites.net',
  'nz.catalyst.adgile.com': 'https://adgile-portalapi-nz.azurewebsites.net',
  'ca.catalyst.adgile.com': 'https://adgile-portalapi-ca.azurewebsites.net',
};

const ATC_API_SERVERS = {
  localhost: 'http://localhost:5000',
  'dev.catalyst.adgile.com': 'https://tc-api-ca-dev.grayflower-d0b44f71.australiaeast.azurecontainerapps.io',
  'catalyst.adgile.com': 'https://tc-api-ca-au.grayflower-d0b44f71.australiaeast.azurecontainerapps.io',
};

const PREFERRED_DOMAIN = 'catalyst.adgile.com';

const DOMAIN_PREFIXES = ['dev.', 'stage.', 'live.', 'au.', 'nz.', 'ca.'];
const PUBLIC_ROUTES = new Set(['/signup', '/transparency-centre/signup']);

function normalizePathname(pathname) {
  if (!pathname || pathname === '/') {
    return '/';
  }

  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

function isPublicRoute(pathname) {
  return PUBLIC_ROUTES.has(normalizePathname(pathname));
}

/**
 * @returns {string|null} the url to redirect to or null if no redirect is needed
 * @param {string} inputHostname
 */
function getPreferredUrl(inputHostname) {
  let hostname = inputHostname ? inputHostname.toLowerCase() : '';
  if (hostname === 'localhost' || hostname === PREFERRED_DOMAIN) {
    return null;
  }
  if (!hostname.endsWith(PREFERRED_DOMAIN)) {
    // definitely redirect to correct prefix
    for (const prefix of DOMAIN_PREFIXES) {
      if (hostname.startsWith(prefix)) {
        return prefix + PREFERRED_DOMAIN;
      }
    }
  } else {
    for (const prefix of DOMAIN_PREFIXES) {
      if (hostname.startsWith(prefix)) {
        // correct domain and prefix
        let preferred = prefix + PREFERRED_DOMAIN;
        if (preferred === hostname) {
          return null;
        } else {
          return preferred;
        }
      }
    }
  }
  // If we get to here our prefix is unknown let the login handle the prefix
  return PREFERRED_DOMAIN;
}

function getAtcApiServer(inputHostname) {
  const hostname = inputHostname ? inputHostname.toLowerCase() : '';

  return ATC_API_SERVERS[hostname] || process.env.VITE_ATC_API_SERVER || process.env.ATC_API_SERVER;
}

function getAuth0CacheLocation(inputHostname) {
  // Local ATC development needs Auth0's own cache to survive hard refreshes.
  // This does not change the Catalyst/ATC boundary: tokens are not passed by attributes, URLs, or events.
  return inputHostname && inputHostname.toLowerCase() === 'localhost' ? 'localstorage' : 'memory';
}

let redirect = getPreferredUrl(window.location.hostname);
if (redirect) {
  console.log('REDIRECTING TO', redirect);
  window.location.host = redirect;
} else {
  // App
  const App = lazy(() => import('./App'));

  Object.fromEntries = Object.fromEntries || (arr => Object.assign({}, ...Array.from(arr, ([k, v]) => ({ [k]: v }))));

  // A function that routes the user to the right place
  // after login
  const onRedirectCallback = appState => {
    const targetUrl = appState && appState.targetUrl ? appState.targetUrl : window.location.pathname;

    window.history.replaceState({}, document.title, targetUrl);
  };

  let api_server = ACTIVE_SERVERS[window.location.hostname.toLowerCase()];
  if (api_server) {
    window.API_SERVER = api_server;
  }
  window.ATC_API_SERVER = getAtcApiServer(window.location.hostname);
  window.ATC_AUTH0_AUDIENCE = process.env.ATC_AUTH0_AUDIENCE;
  window.ATC_AUTH0_DOMAIN = process.env.ATC_AUTH0_DOMAIN;
  const auth0CacheLocation = getAuth0CacheLocation(window.location.hostname);
  const isPublicPath = isPublicRoute(window.location.pathname);
  console.warn('API', api_server, 'from', window.location.hostname);

  if (isPublicPath) {
    const SignupApp = lazy(() => import('./SignupApp'));
    ReactDOM.render(
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Suspense
          fallback={
            <div className="splash">
              <div>
                <img
                  alt="Adgile"
                  width="300px"
                  src={themeBrandingTokens.catalystLightLogo}
                />
              </div>
              <div>LOADING...</div>
            </div>
          }
        >
          <SignupApp />
        </Suspense>
      </ThemeProvider>,
      document.getElementById('root')
    );
  } else {
    ReactDOM.render(
      <Auth0Provider
        domain="adgile.au.auth0.com"
        client_id={process.env.CLIENT_ID}
        audience={window.API_SERVER}
        cacheLocation={auth0CacheLocation}
        redirect_uri={window.location.origin}
        onRedirectCallback={onRedirectCallback}
      >
        <PrivateBoundary>
          <CacheProvider>
            <Suspense
              fallback={
                <div className="splash">
                  <div>
                    <img
                      alt="Adgile"
                      width="300px"
                      src={themeBrandingTokens.catalystLightLogo}
                    />
                  </div>
                  <div>LOADING...</div>
                </div>
              }
            >
              <App />
            </Suspense>
          </CacheProvider>
        </PrivateBoundary>
      </Auth0Provider>,
      document.getElementById('root')
    );
  }
}

navigator.serviceWorker.register(new URL('zipworker.js', import.meta.url), { type: 'module' });
